import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaChevronUp, FaTrashAlt, FaCopy, FaDownload, FaCheckCircle, FaArrowUp } from 'react-icons/fa';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import categories from './categories.json';
import Notification from './Notification';

gsap.registerPlugin(ScrollToPlugin);

const keywords = [
  "Builders", "Construction", "Developers", "Contractors", "Enterprises", 
  "Roofers", "Worx", "Projects", "Creations", "Solutions"
];

const generateCombinations = (categoryKeywords) => {
  let combinations = new Set();
  const suffixes = keywords;

  const loopLimit = categoryKeywords.length * suffixes.length * 2;

  for (let i = 0; i < loopLimit && combinations.size < 20; i++) {
    const prefix = categoryKeywords[Math.floor(Math.random() * categoryKeywords.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    combinations.add(`${prefix} ${suffix}`);
    combinations.add(`${suffix} ${prefix}`);
  }

  return Array.from(combinations).slice(0, 20);
};

const ConstructionNameGenerator = () => {
  const [generatedNames, setGeneratedNames] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [collapsed, setCollapsed] = useState(Object.keys(categories).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
  const [notification, setNotification] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const h1Ref = useRef(null);

  useEffect(() => {
    gsap.to(window, { scrollTo: { y: 0 }, duration: 0 }); // Ensure the page starts at the top

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowScrollButton(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (h1Ref.current) {
      observer.observe(h1Ref.current);
    }

    return () => {
      if (h1Ref.current) {
        observer.unobserve(h1Ref.current);
      }
    };
  }, []);

  const handleGenerate = () => {
    const newGeneratedNames = {};
    for (const category in categories) {
      newGeneratedNames[category] = generateCombinations(categories[category]);
    }
    setGeneratedNames(newGeneratedNames);

    document.getElementById('results').classList.add('visible');
    gsap.to(window, { scrollTo: { y: "#generator-title", autoKill: false }, duration: 2, ease: "power2.inOut" });
  };

  const scrollToTop = () => {
    gsap.to(window, { scrollTo: { y: 0, autoKill: false }, duration: 2, ease: "power2.inOut" });
  };

  const addToFavorites = (name) => {
    if (!favorites.includes(name)) {
      setFavorites([...favorites, name]);
      setNotification(`Agregado "${name}" a favoritos`);
    }
  };

  const removeFromFavorites = (name) => {
    setFavorites(favorites.filter(fav => fav !== name));
  };

  const copyFavorites = () => {
    navigator.clipboard.writeText(favorites.join("\n"));
    alert("Favorites copied to clipboard.");
  };

  const downloadFavorites = () => {
    const element = document.createElement("a");
    const file = new Blob([favorites.join("\n")], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "favorites.txt";
    document.body.appendChild(element);
    element.click();
  };

  const toggleCollapse = (category) => {
    setCollapsed(prevState => ({ ...prevState, [category]: !prevState[category] }));
  };

  return (
    <div className="p-6 bg-gray-50 shadow-md rounded-lg max-w-4xl mx-auto relative">
      <h1 ref={h1Ref} id="generator-title" className="text-3xl font-bold mb-6 text-center text-gray-800">Generador de nombres de compañias de construcción</h1>

      {notification && (
        <Notification message={notification} onClose={() => setNotification(null)} />
      )}

      {favorites.length > 0 && (
        <>
          <h3 className="text-2xl font-bold mt-4 mb-2 text-gray-800 text-center">Nombres Favoritos</h3>
          <ul className="mb-6">
            {favorites.map((name, index) => (
              <li key={index} className="mb-2 flex justify-between items-center bg-white py-3 px-4 rounded shadow-md">
                {name}
                <button onClick={() => removeFromFavorites(name)} className="text-red-600 hover:text-red-800 transition duration-300">
                  <FaTrashAlt />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="flex justify-center mb-6 space-x-4">
        <button
          onClick={copyFavorites}
          className="bg-green-500 text-white py-2 px-4 rounded-full max-w-xs flex items-center justify-center hover:bg-green-700 transition duration-300"
        >
          <FaCopy />
        </button>
        <button
          onClick={downloadFavorites}
          className="bg-gray-500 text-white py-2 px-4 rounded-full max-w-xs flex items-center justify-center hover:bg-gray-800 transition duration-300"
        >
          <FaDownload />
        </button>
      </div>
      <div id="results" className="generated-results">
        {Object.keys(generatedNames).length > 0 && (
          <>
            {Object.keys(categories).map((category) => (
              <div key={category} className="mb-6">
                <div 
                  className="flex justify-between items-center bg-white py-3 px-4 rounded shadow-md cursor-pointer hover:bg-gray-100 transition duration-300"
                  onClick={() => toggleCollapse(category)}
                >
                  <h2 className="text-xl font-bold">{category}</h2>
                  {collapsed[category] ? <FaChevronDown /> : <FaChevronUp />}
                </div>
                {!collapsed[category] && (
                  <ul className="mt-2">
                    {generatedNames[category].map((name, index) => (
                      <li key={index} className="mb-2">
                        <button 
                          onClick={() => addToFavorites(name)} 
                          className="flex justify-between items-center bg-gray-200 w-full py-3 px-4 rounded shadow-md hover:bg-gray-300 transition duration-300"
                        >
                          <span>{name}</span>
                          {favorites.includes(name) ? (
                            <FaCheckCircle className="text-green-500" />
                          ) : (
                            <span className="text-xl">→</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </>
        )} 
      </div>

      <div className="fixed left-0 right-0 mx-auto bottom-6 w-full flex justify-between px-10">
        <button
          onClick={handleGenerate}
          className="bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-700 transition duration-300"
        >
          Generar nombres 
        </button>
        {showScrollButton && (
          <button
            onClick={scrollToTop}
            className="bg-gray-600 text-white py-2 px-4 rounded-full hover:bg-gray-700 transition duration-300"
          >
            <FaArrowUp />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConstructionNameGenerator;
